import {
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    MenuItem,
    Paper,
    Switch,
    TextField,
    Typography,
    useMediaQuery,
} from "@material-ui/core"
import { makeStyles, useTheme } from "@material-ui/core/styles"
import { Receipt, Save, Settings } from "@material-ui/icons"
import { useSnackbar } from "../../../utils/notificacoes"
import React, { useEffect } from "react"
import { ConfiguracaoFiscalActionsTypes } from "../context/configuracaoFiscalActions"
import { useConfiguracaoFiscalContext } from "../context/useConfiguracaoFiscalContext"

const brl = new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" })

const useStyles = makeStyles(theme => ({
    container: {
        paddingLeft: "90px",
        paddingRight: "10px",
        paddingTop: "100px",
        paddingBottom: "30px",
        [theme.breakpoints.down("md")]: { paddingLeft: "20px", paddingRight: "20px", paddingTop: "80px" },
        [theme.breakpoints.down("sm")]: { paddingLeft: "15px", paddingRight: "15px", paddingTop: "70px" },
    },
    content: {
        margin: "auto",
        maxWidth: "800px",
        [theme.breakpoints.down("sm")]: { maxWidth: "100%" },
    },
    titleText: {
        fontWeight: 600,
        color: "#2c3e50",
        [theme.breakpoints.down("sm")]: { fontSize: "1.5rem" },
        [theme.breakpoints.down("xs")]: { fontSize: "1.25rem" },
    },
    configCard: {
        borderRadius: 20,
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        backgroundColor: "white",
        overflow: "hidden",
        marginBottom: theme.spacing(3),
        [theme.breakpoints.down("sm")]: { borderRadius: 15, marginBottom: theme.spacing(2) },
    },
    cardHeader: {
        backgroundColor: "#f8f9fa",
        padding: theme.spacing(2.5),
        borderBottom: "2px solid #e9ecef",
        display: "flex",
        alignItems: "center",
        [theme.breakpoints.down("sm")]: { padding: theme.spacing(2) },
    },
    cardHeaderTitle: {
        fontWeight: 600,
        color: "#2c3e50",
        [theme.breakpoints.down("sm")]: { fontSize: "1.1rem" },
    },
    cardContent: {
        padding: theme.spacing(3),
        [theme.breakpoints.down("sm")]: { padding: theme.spacing(2) },
    },
    iconHeader: {
        color: "#4caf50",
        marginRight: theme.spacing(1),
    },
    fieldRow: {
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: theme.spacing(3),
        marginBottom: theme.spacing(3),
        [theme.breakpoints.down("sm")]: {
            gridTemplateColumns: "1fr",
            gap: theme.spacing(2),
            marginBottom: theme.spacing(2),
        },
    },
    fieldFull: {
        marginBottom: theme.spacing(3),
        [theme.breakpoints.down("sm")]: { marginBottom: theme.spacing(2) },
    },
    hint: {
        color: "#7f8c8d",
        fontSize: "0.82rem",
        marginTop: theme.spacing(0.5),
    },
    switchRow: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: theme.spacing(1.5, 2),
        backgroundColor: "#f8f9fa",
        borderRadius: 12,
        marginBottom: theme.spacing(2),
    },
    emissoesInfo: {
        display: "inline-flex",
        alignItems: "center",
        gap: theme.spacing(1),
        padding: theme.spacing(0.5, 1.5),
        borderRadius: 10,
        backgroundColor: "#e9ecef",
        fontSize: "0.88rem",
        marginBottom: theme.spacing(2),
    },
    formasGrid: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
        gap: theme.spacing(1),
    },
    formaItem: {
        backgroundColor: "#f7f8fb",
        border: "1px solid #e5e7eb",
        borderRadius: 8,
        padding: theme.spacing(0.5, 1.5),
    },
    saveButton: {
        borderRadius: 25,
        backgroundColor: "#4caf50",
        color: "white",
        padding: theme.spacing(1.5, 4),
        fontWeight: 600,
        boxShadow: "0 4px 15px rgba(76, 175, 80, 0.3)",
        minWidth: "200px",
        "&:hover": {
            backgroundColor: "#45a049",
            transform: "translateY(-2px)",
            boxShadow: "0 6px 20px rgba(76, 175, 80, 0.4)",
        },
        [theme.breakpoints.down("sm")]: { minWidth: "100%", padding: theme.spacing(2, 3) },
    },
}))

export function ConfiguracaoFiscalForm() {
    const classes = useStyles()
    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"))
    const { dados, dispatch, erroCarregamento, salvar } = useConfiguracaoFiscalContext()
    const { enqueueSnackbar } = useSnackbar()

    const alertStart = (msg: string, tipo: "success" | "error" | "info") => {
        enqueueSnackbar(msg, { variant: tipo })
    }

    useEffect(() => {
        if (erroCarregamento) alertStart(erroCarregamento, "error")
    }, [erroCarregamento])

    const handleCampo = (campo: keyof typeof dados, valor: string | boolean) => {
        dispatch({ type: ConfiguracaoFiscalActionsTypes.ATUALIZAR_CAMPO, payload: { campo, valor } })
    }

    const handleToggleForma = (id: number) => {
        dispatch({ type: ConfiguracaoFiscalActionsTypes.TOGGLE_FORMA, payload: { id } })
    }

    const handleSalvar = async () => {
        try {
            await salvar()
            alertStart("Configuração fiscal salva com sucesso!", "success")
        } catch (error) {
            alertStart((error as Error).message || "Erro ao salvar configuração fiscal", "error")
        }
    }

    const limiteAtingido =
        dados.limiteDiario !== "" &&
        parseFloat(dados.limiteDiario) > 0 &&
        dados.emissoesHoje >= parseFloat(dados.limiteDiario)

    return (
        <div className={classes.container}>
            <div className={classes.content}>
                <Box mb={isMobile ? 2 : 3}>
                    <Typography variant="h4" className={classes.titleText}>
                        Configuração Fiscal
                    </Typography>
                    <Typography variant="subtitle1" style={{ color: "#7f8c8d" }}>
                        Gerencie a emissão automática de cupons fiscais
                    </Typography>
                </Box>

                {/* Status */}
                <div className={classes.emissoesInfo}>
                    <Receipt style={{ fontSize: 16, color: "#7f8c8d" }} />
                    <span>
                        Valor de emissões: <strong>{brl.format(dados.emissoesHoje || 0)}</strong>
                        {dados.limiteDiario !== "" && parseFloat(dados.limiteDiario) > 0 && (
                            <>
                                {" / "}
                                <strong
                                    style={{ color: limiteAtingido ? "#721c24" : "#155724" }}
                                >
                                    {brl.format(parseFloat(dados.limiteDiario))}
                                </strong>
                                {limiteAtingido && (
                                    <span style={{ color: "#721c24", marginLeft: 6 }}>
                                        — Limite atingido
                                    </span>
                                )}
                            </>
                        )}
                    </span>
                </div>

                {/* Integração PlugNotas */}
                <Paper className={classes.configCard}>
                    <Box className={classes.cardHeader}>
                        <Settings className={classes.iconHeader} />
                        <Typography variant="h6" className={classes.cardHeaderTitle}>
                            Integração PlugNotas
                        </Typography>
                    </Box>
                    <Box className={classes.cardContent}>
                        <div className={classes.fieldRow}>
                            <div>
                                <TextField
                                    label="CNPJ na PlugNotas"
                                    variant="outlined"
                                    fullWidth
                                    size="small"
                                    placeholder="Somente números"
                                    value={dados.cnpjPlugNotas}
                                    onChange={e => {
                                        const value = e.target.value
                                        handleCampo("cnpjPlugNotas", value)
                                    }}
                                    disabled={!!erroCarregamento}
                                />
                                <Typography className={classes.hint}>
                                    CNPJ cadastrado como emitente na PlugNotas para este
                                    estabelecimento.
                                </Typography>
                            </div>
                            <div>
                                <TextField
                                    select
                                    label="Tipo de emissão"
                                    variant="outlined"
                                    fullWidth
                                    size="small"
                                    value={dados.tipoEmissao}
                                    onChange={e => {
                                        const value = e.target.value
                                        handleCampo("tipoEmissao", value)
                                    }}
                                    disabled={!!erroCarregamento}
                                >
                                    <MenuItem value="EMISSAO_COM_IMPRESSAO">
                                        Emissão de cupom com impressão
                                    </MenuItem>
                                    <MenuItem value="EMISSAO_SEM_IMPRESSAO">
                                        Emissão de cupom sem impressão
                                    </MenuItem>
                                </TextField>
                                <Typography className={classes.hint}>
                                    Define se, após cada cupom emitido, será gerada uma solicitação
                                    de impressão no painel.
                                </Typography>
                            </div>
                        </div>

                        <div className={classes.fieldRow}>
                            <div>
                                <Box className={classes.switchRow}>
                                    <Box>
                                        <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                                            Emissão automática
                                        </Typography>
                                        <Typography className={classes.hint}>
                                            Emite cupom quando o pedido é finalizado
                                        </Typography>
                                    </Box>
                                    <Switch
                                        checked={dados.emissaoAutomatica}
                                        onChange={e => {
                                            const checked = e.target.checked
                                            handleCampo("emissaoAutomatica", checked)
                                        }}
                                        color="primary"
                                        disabled={
                                            !!erroCarregamento || !dados.cnpjPlugNotas.trim()
                                        }
                                    />
                                </Box>
                                {!dados.cnpjPlugNotas.trim() && (
                                    <Typography
                                        className={classes.hint}
                                        style={{ color: "#856404" }}
                                    >
                                        Informe o CNPJ na PlugNotas antes de ativar a emissão
                                        automática.
                                    </Typography>
                                )}
                            </div>
                            <div>
                                <TextField
                                    label="Valor máximo de emissões diárias"
                                    variant="outlined"
                                    fullWidth
                                    size="small"
                                    type="number"
                                    inputProps={{ min: 0, step: "0.01" }}
                                    placeholder="Sem limite"
                                    value={dados.limiteDiario}
                                    onChange={e => {
                                        const value = e.target.value
                                        handleCampo("limiteDiario", value)
                                    }}
                                    disabled={!!erroCarregamento}
                                />
                                <Typography className={classes.hint}>
                                    Valor máximo (em R$) de emissões por dia. Deixe vazio ou 0 para
                                    sem limite.
                                </Typography>
                            </div>
                        </div>
                    </Box>
                </Paper>

                {/* E-mails de fechamento */}
                <Paper className={classes.configCard}>
                    <Box className={classes.cardHeader}>
                        <Receipt className={classes.iconHeader} />
                        <Typography variant="h6" className={classes.cardHeaderTitle}>
                            E-mails para fechamento fiscal mensal
                        </Typography>
                    </Box>
                    <Box className={classes.cardContent}>
                        <div className={classes.fieldFull}>
                            <TextField
                                variant="outlined"
                                fullWidth
                                size="small"
                                placeholder="contador@empresa.com, financeiro@empresa.com"
                                value={dados.emailsFechamentoFiscal}
                                onChange={e => {
                                    const value = e.target.value
                                    handleCampo("emailsFechamentoFiscal", value)
                                }}
                                disabled={!!erroCarregamento}
                            />
                            <Typography className={classes.hint}>
                                E-mails separados por vírgula que receberão o ZIP com os XMLs de
                                NFC-e emitidos no mês.
                            </Typography>
                        </div>
                    </Box>
                </Paper>

                {/* Formas de pagamento */}
                <Paper className={classes.configCard}>
                    <Box className={classes.cardHeader}>
                        <Settings className={classes.iconHeader} />
                        <Typography variant="h6" className={classes.cardHeaderTitle}>
                            Formas de pagamento elegíveis
                        </Typography>
                    </Box>
                    <Box className={classes.cardContent}>
                        <Typography className={classes.hint} style={{ marginBottom: 16 }}>
                            Apenas pedidos com as formas marcadas serão emitidos automaticamente.
                            Se nenhuma for marcada, todas são consideradas elegíveis.
                        </Typography>
                        {dados.formas.length === 0 ? (
                            <Typography style={{ color: "#7f8c8d" }}>
                                Nenhuma forma de pagamento cadastrada.
                            </Typography>
                        ) : (
                            <div className={classes.formasGrid}>
                                {dados.formas.map(forma => (
                                    <div key={forma.id} className={classes.formaItem}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={forma.selecionada}
                                                    onChange={() => handleToggleForma(forma.id)}
                                                    color="primary"
                                                    disabled={!!erroCarregamento}
                                                    size="small"
                                                />
                                            }
                                            label={forma.descricao}
                                        />
                                    </div>
                                ))}
                            </div>
                        )}
                    </Box>
                </Paper>

                {/* Botão salvar */}
                <Box display="flex" justifyContent="center" mt={2}>
                    <Button
                        variant="contained"
                        className={classes.saveButton}
                        startIcon={<Save />}
                        onClick={handleSalvar}
                        disabled={!!erroCarregamento}
                    >
                        Salvar Configuração
                    </Button>
                </Box>
            </div>
        </div>
    )
}
